<template>
    <q-dialog :model-value="show" @hide="emitCloseDialogEvent">
        <q-card
            id="download-modal"
            class="phase-file-ready-dialog"
            style="position: relative"
        >
            <q-btn
                id="download-modal-close"
                @click="emitCloseDialogEvent"
                dense
                flat
                icon="close"
                style="position: absolute; top: 15px; right: 15px; z-index: 1"
            >
                <q-tooltip>{{ $t('Close') }}</q-tooltip>
            </q-btn>

            <div class="q-pa-lg text-center">
                <!-- Success Icon and Title -->
                <div class="q-mt-md">
                    <q-icon
                        name="download"
                        color="positive"
                        size="3rem"
                        class="block q-mb-sm"
                        style="margin-left: auto; margin-right: auto"
                    />
                    <h3
                        id="download-started-title"
                        class="q-ma-none text-h5 text-weight-medium text-dark"
                    >
                        {{ $t('Download started') }}
                    </h3>
                </div>

                <!-- Download Note -->
                <div class="q-mb-lg q-mt-md flex justify-center">
                    <p
                        id="download-note"
                        class="q-ma-none text-body2 text-grey-5"
                        style="max-width: 250px"
                    >
                        {{
                            $t(
                                "File is currently downloading, you'll find it in your downloads folder."
                            )
                        }}
                    </p>
                </div>

                <div v-if="!isLinkExpired">
                    <!-- Download Link -->
                    <div class="q-mb-xs q-mt-xl">
                        <a
                            id="restart-download-link"
                            :href="linkedUrl"
                            :class="{
                                'text-grey-7': isLinkExpired,
                                'text-primary': !isLinkExpired
                            }"
                            class="text-body2 cursor-pointer"
                            :style="
                                isLinkExpired ? 'pointer-events: none;' : ''
                            "
                        >
                            {{ $t('Click here to download the file manually') }}
                        </a>
                    </div>

                    <!-- Link Expiration Notice -->
                    <div class="q-mb-md">
                        <p
                            id="link-expiration-text"
                            class="q-ma-none text-caption text-grey-7"
                        >
                            {{ countdownText }}
                        </p>
                    </div>
                </div>
            </div>
        </q-card>
    </q-dialog>
</template>
<script>
/**
 * Modal component for displaying download ready status with countdown timer.
 */
export default {
    name: 'PhaseFileReadyModal',
    props: {
        linkedUrl: {
            type: String,
            required: true
        }
    },
    emits: {
        closeDialog: null
    },
    data() {
        return {
            show: true,
            countdownInterval: null,
            timeRemaining: 0
        };
    },
    computed: {
        /**
         * Formats the countdown text for display.
         * @returns {string} Formatted countdown text or expiration message.
         */
        countdownText() {
            if (this.timeRemaining <= 0) {
                return this.$t('Link has expired');
            }

            const minutes = Math.floor(this.timeRemaining / 60);
            const seconds = this.timeRemaining % 60;

            return this.$t('Link expires in: {minutes}m {seconds}s', {
                minutes,
                seconds: seconds.toString().padStart(2, '0')
            });
        },

        /**
         * Determines if the download link has expired.
         * @returns {boolean} True if the link is expired, false otherwise.
         */
        isLinkExpired() {
            return this.timeRemaining <= 0;
        }
    },
    mounted() {
        this.parseExpirationTime();
        this.startCountdown();
        this.downloadFile();
    },
    beforeUnmount() {
        this.clearCountdown();
    },
    methods: {
        /**
         * Parses the expiration time from the download URL.
         */
        parseExpirationTime() {
            try {
                const url = new URL(this.linkedUrl);
                const seParam = url.searchParams.get('se');

                if (seParam) {
                    const expirationTime = new Date(seParam);
                    const currentTime = new Date();
                    const timeDiff = Math.floor(
                        (expirationTime - currentTime) / 1000
                    );

                    this.timeRemaining = Math.max(0, timeDiff);
                } else {
                    // Fallback if no 'se' parameter found
                    this.timeRemaining = 0;
                }
            } catch (error) {
                console.warn(
                    'Failed to parse expiration time from URL:',
                    error
                );
                this.timeRemaining = 0;
            }
        },

        /**
         * Starts the countdown timer that updates every second.
         */
        startCountdown() {
            this.countdownInterval = setInterval(() => {
                if (this.timeRemaining > 0) {
                    this.timeRemaining--;
                } else {
                    this.clearCountdown();
                }
            }, 1000);
        },

        /**
         * Clears the countdown interval.
         */
        clearCountdown() {
            if (this.countdownInterval) {
                clearInterval(this.countdownInterval);
                this.countdownInterval = null;
            }
        },

        /**
         * Automatically downloads the file by creating a temporary anchor element.
         */
        downloadFile() {
            if (this.linkedUrl && !this.isLinkExpired) {
                const link = document.createElement('a');
                link.href = this.linkedUrl;
                link.download = '';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        },

        /**
         * Emits the close dialog event to parent component.
         */
        emitCloseDialogEvent() {
            this.clearCountdown();
            this.$emit('closeDialog');
        }
    }
};
</script>

<style lang="scss" scoped>
.phase-file-ready-dialog {
    width: 100%;
    max-width: 400px;
}
</style>
